"use client"

import Image from "next/image"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"
import localFont from "next/font/local"
import { Heart, History, MessageCircle, User, X, Loader2, CheckCircle, AlertCircle } from "lucide-react"
import { createContext, useContext, useState, useTransition, useCallback, type ReactNode } from "react"
import { subscribeToMailchimp } from "@/app/actions/mailchimp"
import { InlineLegalNotice } from "@/components/legal-links"

// Exposure font for headings (regular only, no italic)
export const exposureFont = localFont({
  src: "../../public/fonts/205TF-Exposure-[-40].woff2",
  variable: "--font-exposure",
})

// Colors from brand design
export const colors = {
  cyan: "#62D6CE",
  pink: "#E16B98",
  cream: "#F5F3E8",
  black: "#000000",
  red: "#E72214",
}

interface MarqueeBannerProps {
  backgroundColor?: string
  textColor?: string
  message?: string
}

export function MarqueeBanner({
  backgroundColor = colors.black,
  textColor = colors.cyan,
  message = "We're in beta! Expect rough edges but great finds",
}: MarqueeBannerProps) {
  return (
    <div
      className="py-3 overflow-hidden"
      style={{ backgroundColor }}
    >
      {/* Mobile: Single centered message */}
      <div className="md:hidden text-center px-4">
        <span
          className="text-sm font-bold tracking-tight"
          style={{ color: textColor }}
        >
          {message}
          <span
            className="ml-3 inline-block"
            style={{ transform: "scaleY(1.3)" }}
          >
            ✦
          </span>
        </span>
      </div>
      
      {/* Desktop: Static repeated messages */}
      <div className="hidden md:flex justify-center whitespace-nowrap">
        {[...Array(3)].map((_, i) => (
          <span
            key={i}
            className="mx-8 text-lg lg:text-xl font-bold tracking-tight"
            style={{ color: textColor }}
          >
            {message}
            <span
              className="mx-6 inline-block"
              style={{ transform: "scaleY(1.3)" }}
            >
              ✦
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

interface NavProps {
  logoColor?: "cyan" | "white" | "black" | "red"
  backgroundColor?: string
  textColor?: string
  showEarlyAccess?: boolean
  earlyAccessStyle?: "text" | "button"
  earlyAccessButtonColor?: string
  earlyAccessTextColor?: string
  aboutUsHref?: string
  showAboutUs?: boolean
  onEarlyAccessClick?: () => void
}

export function Nav({
  logoColor = "cyan",
  backgroundColor = colors.black,
  textColor = "white",
  showEarlyAccess = true,
  earlyAccessStyle = "button",
  earlyAccessButtonColor = colors.cyan,
  earlyAccessTextColor = colors.black,
  aboutUsHref = "/about-us",
  showAboutUs = true,
  onEarlyAccessClick,
}: NavProps) {
  // Logo color filters
  const logoFilters = {
    cyan: "brightness(0) saturate(100%) invert(76%) sepia(31%) saturate(487%) hue-rotate(121deg) brightness(93%) contrast(89%)",
    white: "brightness(0) invert(1)",
    black: "brightness(0)",
    red: "brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(4744%) hue-rotate(355deg) brightness(97%) contrast(88%)",
  }

  return (
    <nav
      className="relative flex items-center justify-between px-6 lg:px-16 py-6 md:py-8 lg:py-10 min-h-[80px] md:min-h-[120px]"
      style={{ backgroundColor }}
    >
      {/* Logo - Left on mobile, center on desktop */}
      <div className="relative md:absolute md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2">
        <Link href="/">
          <Image
            src="/landing/logo-loop-home.png"
            alt="Loop Home"
            width={120}
            height={100}
            className="h-16 md:h-20 w-auto"
            style={{ filter: logoFilters[logoColor] }}
          />
        </Link>
      </div>

      {/* Right: About Us + Early Access */}
      <div className="flex items-center gap-3 md:gap-6 ml-auto relative z-10">
        {/* About Us Link */}
        {showAboutUs && (
          <Link
            href={aboutUsHref}
            className="text-xs md:text-sm font-medium hover:opacity-100 transition-colors whitespace-nowrap"
            style={{ color: textColor, opacity: 0.7 }}
          >
            About Us
          </Link>
        )}

        {/* Early Access */}
        {showEarlyAccess && (
          earlyAccessStyle === "button" ? (
            <button
              onClick={onEarlyAccessClick}
              className="inline-block px-4 md:px-8 py-2 md:py-2.5 text-xs md:text-sm font-bold tracking-wide rounded-full transition-all hover:scale-105 uppercase flex-shrink-0"
              style={{ backgroundColor: earlyAccessButtonColor, color: earlyAccessTextColor }}
            >
              Early Access
            </button>
          ) : (
            <button
              onClick={onEarlyAccessClick}
              className="text-xs md:text-sm font-medium hover:opacity-100 transition-colors whitespace-nowrap"
              style={{ color: textColor, opacity: 0.7 }}
            >
              Early Access
            </button>
          )
        )}
      </div>
    </nav>
  )
}

interface ImageArcProps {
  variant?: "dark" | "light"
}

export function ImageArc({ variant = "dark" }: ImageArcProps) {
  // TRUE RADIAL ARC: Images on rays from a circle center
  // Desktop: 7 images, 180° arc split into 6 equal 30° segments
  // Mobile: 5 images (hide ±90° outer tier), tighter radius

  const images = [
    { src: "/landing/collage-10.png", hideOnMobile: true },   // Ray 0: -90° (hidden on mobile)
    { src: "/landing/collage-11.jpg", hideOnMobile: false },  // Ray 1: -60°
    { src: "/landing/collage-12.jpg", hideOnMobile: false },  // Ray 2: -30°
    { src: "/landing/collage-13.png", hideOnMobile: false },  // Ray 3:   0° (top center)
    { src: "/landing/collage-14.png", hideOnMobile: false },  // Ray 4: +30°
    { src: "/landing/collage-15.png", hideOnMobile: false },  // Ray 5: +60°
    { src: "/landing/collage-16.png", hideOnMobile: true }, 
  ]

  // Calculate positions on rays emanating from center
  const imageConfig = images.map((img, index) => {
    // 7 images = 6 gaps, 180° / 6 = 30° per gap
    const angleDeg = -90 + index * 30
    const angleRad = (angleDeg * Math.PI) / 180

    // Normalized position multipliers (will be scaled by radius)
    const xMultiplier = Math.sin(angleRad) // -1 to +1
    const yMultiplier = 1 - Math.cos(angleRad) // 0 to 2

    return {
      src: img.src,
      hideOnMobile: img.hideOnMobile,
      xMultiplier,
      yMultiplier,
      rotation: angleDeg,
    }
  })

  // Pre-calculate positions for mobile and desktop
  // Increased radii so outer images go off screen edges
  const mobileRadius = 48
  const desktopRadius = 52

  const positions = imageConfig.map((img) => ({
    ...img,
    mobile: {
      x: mobileRadius * img.xMultiplier,
      y: 10 + mobileRadius * img.yMultiplier * 0.9,
    },
    desktop: {
      x: desktopRadius * img.xMultiplier,
      y: 5 + desktopRadius * img.yMultiplier * 1.6,
    },
  }))

  return (
    <>
      {positions.map((img, index) => (
        <div key={index} className="contents">
          {/* Mobile version */}
          <div
            className={`absolute w-[90px] md:hidden ${img.hideOnMobile ? "hidden" : ""}`}
            style={{
              top: `${img.mobile.y}%`,
              left: `calc(50% + ${img.mobile.x}vw)`,
              transform: "translateX(-50%)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: img.rotation }}
              whileInView={{ opacity: 1, scale: 1, rotate: img.rotation }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.4 }}
              style={{ transformOrigin: "center center" }}
            >
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <Image
                  src={img.src}
                  alt=""
                  width={234}
                  height={264}
                  className="w-full h-auto object-cover"
                  style={{ aspectRatio: "234/264" }}
                />
              </div>
            </motion.div>
          </div>

          {/* Desktop version */}
          <div
            className="absolute hidden md:block md:w-[140px] lg:w-[180px]"
            style={{
              top: `${img.desktop.y}%`,
              left: `calc(50% + ${img.desktop.x}vw)`,
              transform: "translateX(-50%)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: img.rotation }}
              whileInView={{ opacity: 1, scale: 1, rotate: img.rotation }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.4 }}
              style={{ transformOrigin: "center center" }}
            >
              <div className="rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src={img.src}
                  alt=""
                  width={234}
                  height={264}
                  className="w-full h-auto object-cover"
                  style={{ aspectRatio: "234/264" }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      ))}
    </>
  )
}

// App Navigation for chat/product pages
interface AppNavProps {
  onFeedbackClick?: () => void
  onFavouritesClick?: () => void
  onHistoryClick?: () => void
  favouritesCount?: number
  showHistory?: boolean
  userNav?: React.ReactNode
}

export function AppNav({
  onFeedbackClick,
  onFavouritesClick,
  onHistoryClick,
  favouritesCount = 0,
  showHistory = true,
  userNav,
}: AppNavProps) {
  // Logo color filter for cyan
  const cyanFilter = "brightness(0) saturate(100%) invert(76%) sepia(31%) saturate(487%) hue-rotate(121deg) brightness(93%) contrast(89%)"

  return (
    <nav className="relative flex items-center justify-between px-4 md:px-6 lg:px-16 py-4 md:py-6 lg:py-8 min-h-[70px] md:min-h-[100px] lg:min-h-[116px] bg-black">
      {/* Left: Feedback button (hidden on mobile, shown on desktop) */}
      <div className="hidden md:flex items-center">
        <button
          onClick={onFeedbackClick}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
        >
          <span>Feedback</span>
          <MessageCircle className="w-4 h-4" />
        </button>
      </div>

      {/* Logo - Left on mobile, center on desktop */}
      <div className="relative md:absolute md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2">
        <Link href="/">
          <Image
            src="/landing/logo-loop-home.png"
            alt="Loop Home"
            width={120}
            height={100}
            className="h-12 md:h-16 lg:h-20 w-auto"
            style={{ filter: cyanFilter }}
          />
        </Link>
      </div>

      {/* Right: Icons */}
      <div className="flex items-center gap-2 md:gap-4 ml-auto relative z-10">
        {/* Feedback - mobile only */}
        <button
          onClick={onFeedbackClick}
          className="md:hidden p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Feedback"
        >
          <MessageCircle className="w-5 h-5 text-white" />
        </button>

        {/* Favourites */}
        <button
          onClick={onFavouritesClick}
          className="p-2 rounded-full hover:bg-white/10 transition-colors relative"
          aria-label="Favourites"
        >
          <Heart
            className={`w-5 h-5 md:w-6 md:h-6 ${
              favouritesCount > 0 ? "fill-red-500 text-red-500" : "text-white"
            }`}
          />
        </button>

        {/* History */}
        {showHistory && (
          <button
            onClick={onHistoryClick}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Chat history"
          >
            <History className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>
        )}

        {/* User Nav - passed in for flexibility */}
        {userNav}
      </div>
    </nav>
  )
}

// Product/Modal header variant - simpler, for modals and listing pages
interface ProductHeaderProps {
  onClose?: () => void
  onShare?: () => void
  onFavourite?: () => void
  isFavourite?: boolean
  linkCopied?: boolean
  showActions?: boolean
}

export function ProductHeader({
  onClose,
  onShare,
  onFavourite,
  isFavourite = false,
  linkCopied = false,
  showActions = true,
}: ProductHeaderProps) {
  const cyanFilter = "brightness(0) saturate(100%) invert(76%) sepia(31%) saturate(487%) hue-rotate(121deg) brightness(93%) contrast(89%)"

  return (
    <div className="relative flex items-center justify-center px-4 md:px-6 py-4 md:py-6 bg-black">
      {/* Centered Logo */}
      <Link href="/proto-chat-ai-sdk" className="z-10">
        <Image
          src="/landing/logo-loop-home.png"
          alt="Loop Home"
          width={120}
          height={100}
          className="h-10 md:h-14 lg:h-16 w-auto"
          style={{ filter: cyanFilter }}
        />
      </Link>

      {/* Right: Action buttons */}
      {showActions && (
        <div className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {/* Share button */}
          {onShare && (
            <button
              onClick={onShare}
              className="p-2 rounded-full hover:bg-white/10 transition-colors relative"
              aria-label="Share link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white md:w-6 md:h-6"
              >
                {/* Share2 icon - circle nodes with connecting lines */}
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
              </svg>
              {linkCopied && (
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-white bg-black/80 px-2 py-1 rounded whitespace-nowrap">
                  Link copied!
                </span>
              )}
            </button>
          )}

          {/* Favourite button */}
          {onFavourite && (
            <button
              onClick={onFavourite}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
            >
              <Heart
                className={`w-5 h-5 md:w-6 md:h-6 ${
                  isFavourite ? "fill-red-500 text-red-500" : "text-white"
                }`}
              />
            </button>
          )}

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white md:w-6 md:h-6"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// Early Access Modal Context & Component
// ============================================

interface EarlyAccessModalContextType {
  openModal: () => void
}

const EarlyAccessModalContext = createContext<EarlyAccessModalContextType | null>(null)

export function useEarlyAccessModal() {
  const context = useContext(EarlyAccessModalContext)
  if (!context) {
    throw new Error("useEarlyAccessModal must be used within an EarlyAccessModalProvider")
  }
  return context
}

interface EarlyAccessModalProviderProps {
  children: ReactNode
}

export function EarlyAccessModalProvider({ children }: EarlyAccessModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <EarlyAccessModalContext.Provider value={{ openModal }}>
      {children}
      <EarlyAccessModal isOpen={isOpen} onClose={closeModal} />
    </EarlyAccessModalContext.Provider>
  )
}

interface EarlyAccessModalProps {
  isOpen: boolean
  onClose: () => void
}

function EarlyAccessModal({ isOpen, onClose }: EarlyAccessModalProps) {
  const [email, setEmail] = useState("")
  const [isPending, startTransition] = useTransition()
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || isPending) return

    const formData = new FormData()
    formData.append("email", email)

    startTransition(async () => {
      const response = await subscribeToMailchimp(formData, "EARLY_ACCESS")
      setResult(response)

      if (response.success) {
        setEmail("")
        // Auto-close after success
        setTimeout(() => {
          onClose()
          // Reset result after modal closes
          setTimeout(() => setResult(null), 300)
        }, 2000)
      }
    })
  }

  const handleClose = () => {
    if (!isPending) {
      onClose()
      // Reset state after animation
      setTimeout(() => {
        setEmail("")
        setResult(null)
      }, 300)
    }
  }

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !isPending) {
      handleClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onKeyDown={handleKeyDown}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full h-full md:h-auto md:max-w-md md:mx-4 flex flex-col"
            style={{ backgroundColor: colors.cream }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="early-access-title"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={isPending}
              className="absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full hover:bg-black/5 transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-6 h-6 text-black" />
            </button>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-center px-8 py-16 md:px-12 md:py-16">
              {result?.success ? (
                // Success state
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <CheckCircle className="w-16 h-16 mx-auto mb-6" style={{ color: colors.cyan }} />
                  <h2
                    className="text-2xl md:text-3xl mb-4 text-black"
                    style={{ fontFamily: "var(--font-exposure), Georgia, serif" }}
                  >
                    You&apos;re in!
                  </h2>
                  <p className="text-black/70 text-lg">
                    {result.message}
                  </p>
                </motion.div>
              ) : (
                // Form state
                <>
                  <h2
                    id="early-access-title"
                    className="text-3xl md:text-4xl mb-4 text-black"
                    style={{ fontFamily: "var(--font-exposure), Georgia, serif" }}
                  >
                    Join the community
                  </h2>

                  <p className="text-lg md:text-xl text-black/70 mb-8 leading-relaxed">
                    We&apos;re building Loop from the ground up with like-minded people who choose pre-loved. Come and follow the journey.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="early-access-email"
                        className="block text-sm font-bold text-black mb-2"
                      >
                        Email
                      </label>
                      <input
                        id="early-access-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        className="w-full px-4 py-3 border-2 border-black/20 bg-white focus:border-black focus:outline-none transition-colors text-black placeholder:text-black/40"
                        required
                        disabled={isPending}
                        autoFocus
                      />
                    </div>

                    {result && !result.success && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{result.message}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isPending || !email}
                      className="w-full px-8 py-4 text-base font-bold tracking-wide text-black rounded-full transition-all hover:scale-[1.02] hover:shadow-lg uppercase disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                      style={{ backgroundColor: colors.cyan }}
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Joining...</span>
                        </>
                      ) : (
                        <span>Join</span>
                      )}
                    </button>
                    <InlineLegalNotice
                      prefix="By joining, you agree to our"
                      className="text-xs text-black/60"
                      linkClassName="underline underline-offset-2 hover:text-black"
                    />
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
